import axios from "axios";
import React, { useEffect, useState } from "react";
import DataTable from "react-data-table-component";
import DataTableExtensions from "react-data-table-component-extensions";
import "react-data-table-component-extensions/dist/index.css";
import { useNavigate } from "react-router-dom";
import { getApiBaseUrl } from "../../utils/utils";

export default function MedicalAttendanceConfig() {
  const API_URL = process.env.REACT_APP_API_URL;
  const [msg, setMsg] = useState("");
  const [errormsg, setError] = useState("");
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [partners, setPartners] = useState([]);
  const navigate = useNavigate();

  useEffect(() => {
    fetchPartners();
    fetchConfigs();
  }, []);

  const fetchPartners = async () => {
    try {
      const baseApiUrl = getApiBaseUrl();
      const response = await axios.get(
        `${baseApiUrl}/v1.0/partner/retrieve_list?page=0`
      );
      // Get all partners - can be filtered for medical-related if needed
      const allPartners = response.data.data || [];
      // Show all partners or filter by medical-related account types
      const medicalPartners = allPartners.filter(
        (partner) => {
          const accountType = partner.accountType?.toLowerCase() || '';
          // Include medical, lab, hospital, healthcare related or all partners
          return accountType.includes('medical') || 
                 accountType.includes('lab') || 
                 accountType.includes('hospital') ||
                 accountType.includes('health') ||
                 accountType === '' ||
                 true; // Set to true to show all partners
        }
      );
      setPartners(medicalPartners);
      console.log("Fetched partners for medical attendance:", medicalPartners.length, "out of", allPartners.length);
    } catch (err) {
      console.error("Error fetching partners:", err);
      setError("Failed to load partners. Please refresh the page.");
    }
  };

  const fetchConfigs = async () => {
    setLoading(true);
    try {
      const response = await axios.get(
        `${API_URL}shypvdriverapp/api/medical-attendance/config/all`
      );
      if (response.data.success) {
        // Map the response to match expected format
        const configs = (response.data.data || []).map(config => ({
          ...config,
          partnerId: config.partnerId,
          selfieRequired: config.selfieConfig?.enabled || config.selfieConfig?.requiredForCheckIn || false,
          geofenceRequired: config.locationConfig?.required || false,
          active: config.isActive !== false,
          driverCount: config.drivers?.length || 0,
          geofenceCount: config.geofences?.length || 0,
          shiftName: config.shifts?.[0]?.name || 'Default',
          shiftStartTime: config.shifts?.[0]?.startTime || '',
          shiftEndTime: config.shifts?.[0]?.endTime || '',
        }));
        setData(configs);
      } else {
        setError(response.data.message || "Failed to fetch configs");
      }
    } catch (err) {
      setError(err.message || "Error fetching data");
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (config) => {
    navigate("/orders/medical-attendance-setting", {
      state: { config, isEdit: true },
    });
  };

  const handleDelete = async (partnerId) => {
    if (!window.confirm("Are you sure you want to delete this configuration?")) {
      return;
    }
    try {
      const response = await axios.delete(
        `${API_URL}shypvdriverapp/api/medical-attendance/config?partnerId=${partnerId}`
      );
      if (response.data.success) {
        setMsg("Configuration deleted successfully");
        fetchConfigs();
      } else {
        setError(response.data.message || "Failed to delete");
      }
    } catch (err) {
      setError(err.message || "Error deleting config");
    }
  };

  const handleViewDrivers = (partnerId) => {
    navigate("/orders/medical-attendance-drivers", {
      state: { partnerId },
    });
  };

  const handleManageGeofences = (partnerId) => {
    navigate("/orders/medical-attendance-geofence", {
      state: { partnerId },
    });
  };

  const getPartnerName = (partnerId) => {
    const partner = partners.find((p) => p.pid === partnerId);
    return partner ? partner.pname : `Partner ${partnerId}`;
  };

  const columns = [
    {
      name: "#",
      selector: (row, index) => index + 1,
      sortable: false,
      width: "50px",
      hide: "sm",
    },
    {
      name: "Partner",
      selector: (row) => getPartnerName(row.partnerId),
      sortable: true,
      minWidth: "140px",
      wrap: true,
    },
    {
      name: "Shift",
      selector: (row) => row.shiftName || "N/A",
      sortable: true,
      minWidth: "120px",
      hide: "md",
      cell: (row) => (
        <div style={{ padding: "5px 0" }}>
          <div style={{ fontWeight: 500, fontSize: "13px" }}>{row.shiftName || "N/A"}</div>
          {row.shiftStartTime && row.shiftEndTime && (
            <div style={{ fontSize: "11px", color: "#666" }}>
              {row.shiftStartTime} - {row.shiftEndTime}
            </div>
          )}
        </div>
      ),
    },
    {
      name: "Selfie",
      selector: (row) => row.selfieRequired,
      sortable: true,
      width: "90px",
      hide: "sm",
      cell: (row) => (
        <span
          style={{
            padding: "3px 6px",
            borderRadius: "4px",
            fontSize: "11px",
            backgroundColor: row.selfieRequired ? "#d4edda" : "#f8d7da",
            color: row.selfieRequired ? "#155724" : "#721c24",
          }}
        >
          {row.selfieRequired ? "Yes" : "No"}
        </span>
      ),
    },
    {
      name: "GPS",
      selector: (row) => row.geofenceRequired,
      sortable: true,
      width: "80px",
      hide: "sm",
      cell: (row) => (
        <span
          style={{
            padding: "3px 6px",
            borderRadius: "4px",
            fontSize: "11px",
            backgroundColor: row.geofenceRequired ? "#d4edda" : "#f8d7da",
            color: row.geofenceRequired ? "#155724" : "#721c24",
          }}
        >
          {row.geofenceRequired ? "Yes" : "No"}
        </span>
      ),
    },
    {
      name: "Locations",
      selector: (row) => row.geofenceCount || 0,
      sortable: true,
      width: "90px",
      hide: "md",
      cell: (row) => (
        <button
          onClick={() => handleManageGeofences(row.partnerId)}
          style={{
            backgroundColor: "#17a2b8",
            color: "#fff",
            border: "none",
            borderRadius: "4px",
            padding: "4px 8px",
            cursor: "pointer",
            fontSize: "11px",
          }}
        >
          {row.geofenceCount || 0}
        </button>
      ),
    },
    {
      name: "Drivers",
      selector: (row) => row.driverCount || 0,
      sortable: true,
      width: "80px",
      hide: "md",
      cell: (row) => (
        <button
          onClick={() => handleViewDrivers(row.partnerId)}
          style={{
            backgroundColor: "#6c757d",
            color: "#fff",
            border: "none",
            borderRadius: "4px",
            padding: "4px 8px",
            cursor: "pointer",
            fontSize: "11px",
          }}
        >
          {row.driverCount || 0}
        </button>
      ),
    },
    {
      name: "Status",
      selector: (row) => row.active,
      sortable: true,
      width: "80px",
      cell: (row) => (
        <span
          style={{
            padding: "3px 6px",
            borderRadius: "4px",
            fontSize: "11px",
            backgroundColor: row.active ? "#d4edda" : "#f8d7da",
            color: row.active ? "#155724" : "#721c24",
          }}
        >
          {row.active ? "Active" : "Off"}
        </span>
      ),
    },
    {
      name: "Actions",
      minWidth: "120px",
      cell: (row) => (
        <div style={{ display: "flex", gap: "4px", flexWrap: "wrap" }}>
          <button
            onClick={() => handleEdit(row)}
            style={{
              backgroundColor: "#007bff",
              color: "#fff",
              border: "none",
              borderRadius: "4px",
              padding: "5px 10px",
              cursor: "pointer",
              fontSize: "11px",
            }}
          >
            Edit
          </button>
          <button
            onClick={() => handleDelete(row.partnerId)}
            style={{
              backgroundColor: "#dc3545",
              color: "#fff",
              border: "none",
              borderRadius: "4px",
              padding: "5px 10px",
              cursor: "pointer",
              fontSize: "11px",
            }}
          >
            Del
          </button>
        </div>
      ),
      ignoreRowClick: true,
      allowOverflow: true,
      button: true,
    },
  ];

  const tableData = {
    columns,
    data,
  };

  const customStyles = {
    headRow: {
      style: {
        backgroundColor: "#f8f9fa",
        borderBottom: "2px solid #dee2e6",
        minHeight: "45px",
      },
    },
    headCells: {
      style: {
        fontWeight: "600",
        fontSize: "12px",
        color: "#495057",
        paddingLeft: "8px",
        paddingRight: "8px",
      },
    },
    rows: {
      style: {
        minHeight: "50px",
      },
    },
    cells: {
      style: {
        paddingLeft: "8px",
        paddingRight: "8px",
      },
    },
  };

  return (
    <div style={{ padding: "15px" }}>
      <div className="form-container" style={{ maxWidth: "100%", margin: "0 auto" }}>
        <div
          style={{
            display: "flex",
            flexWrap: "wrap",
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: "20px",
            gap: "10px",
          }}
        >
          <h5 style={{ margin: 0, color: "#333", fontSize: "18px" }}>
            <i className="fa fa-medkit" style={{ marginRight: "10px" }}></i>
            Medical Attendance Config
          </h5>
          <button
            onClick={() => navigate("/orders/medical-attendance-setting")}
            style={{
              backgroundColor: "#28a745",
              color: "#fff",
              border: "none",
              borderRadius: "6px",
              padding: "8px 16px",
              cursor: "pointer",
              fontSize: "14px",
              fontWeight: "500",
              display: "flex",
              alignItems: "center",
              gap: "8px",
            }}
          >
            <i className="fa fa-plus"></i> Add New Config
          </button>
        </div>

        {/* Summary Stats */}
        <div
          style={{
            display: "flex",
            flexWrap: "wrap",
            gap: "15px",
            marginBottom: "15px",
          }}
        >
          <div
            style={{
              backgroundColor: "#e3f2fd",
              padding: "10px 15px",
              borderRadius: "6px",
              display: "flex",
              alignItems: "center",
              gap: "8px",
            }}
          >
            <i className="fa fa-building" style={{ color: "#1976d2" }}></i>
            <span style={{ fontSize: "13px", color: "#1976d2" }}>
              <strong>{partners.length}</strong> Medical Partners
            </span>
          </div>
          <div
            style={{
              backgroundColor: "#e8f5e9",
              padding: "10px 15px",
              borderRadius: "6px",
              display: "flex",
              alignItems: "center",
              gap: "8px",
            }}
          >
            <i className="fa fa-cog" style={{ color: "#388e3c" }}></i>
            <span style={{ fontSize: "13px", color: "#388e3c" }}>
              <strong>{data.length}</strong> Configs Active
            </span>
          </div>
        </div>

        {msg && (
          <div
            style={{
              padding: "10px 15px",
              marginBottom: "15px",
              backgroundColor: "#d4edda",
              color: "#155724",
              borderRadius: "6px",
              border: "1px solid #c3e6cb",
            }}
          >
            {msg}
          </div>
        )}

        {errormsg && (
          <div
            style={{
              padding: "10px 15px",
              marginBottom: "15px",
              backgroundColor: "#f8d7da",
              color: "#721c24",
              borderRadius: "6px",
              border: "1px solid #f5c6cb",
            }}
          >
            {errormsg}
          </div>
        )}

        <div
          style={{
            backgroundColor: "#fff",
            borderRadius: "8px",
            boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
            overflow: "hidden",
          }}
        >
          {loading ? (
            <div style={{ padding: "40px", textAlign: "center" }}>
              <i
                className="fa fa-spinner fa-spin"
                style={{ fontSize: "24px", color: "#007bff" }}
              ></i>
              <p style={{ marginTop: "10px", color: "#666" }}>
                Loading configurations...
              </p>
            </div>
          ) : data && data.length > 0 ? (
            <DataTableExtensions {...tableData}>
              <DataTable
                noHeader
                defaultSortField="partnerId"
                pagination
                highlightOnHover
                striped
                responsive
                customStyles={customStyles}
              />
            </DataTableExtensions>
          ) : (
            <div style={{ padding: "40px", textAlign: "center" }}>
              <i
                className="fa fa-inbox"
                style={{ fontSize: "48px", color: "#ccc", marginBottom: "15px", display: "block" }}
              ></i>
              <h4 style={{ color: "#666", marginBottom: "10px" }}>
                No Configurations Found
              </h4>
              <p style={{ color: "#999", marginBottom: "15px" }}>
                Get started by creating your first medical attendance configuration
              </p>
              {partners.length > 0 && (
                <p style={{ color: "#17a2b8", marginBottom: "20px", fontSize: "14px" }}>
                  <i className="fa fa-info-circle" style={{ marginRight: "5px" }}></i>
                  {partners.length} medical partner{partners.length > 1 ? 's' : ''} available
                </p>
              )}
              <button
                onClick={() => navigate("/orders/medical-attendance-setting")}
                style={{
                  backgroundColor: "#28a745",
                  color: "#fff",
                  border: "none",
                  borderRadius: "6px",
                  padding: "10px 20px",
                  cursor: "pointer",
                  fontSize: "14px",
                }}
              >
                <i className="fa fa-plus" style={{ marginRight: "8px" }}></i>
                Create Configuration
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
